import EnumDataSource from "../utils/enum-data-source.util";

export const HZ_VALUES = [
  125, 250, 500, 750, 1000, 1500, 2000, 3000, 4000, 6000, 8000, 12000, 16000
];

export const ULTRA_HIGH_FREQUENCIES = [12000, 16000];

export const getVisibleFrequencies = (hideUltraHigh: boolean): number[] =>
  hideUltraHigh
    ? HZ_VALUES.filter((hz) => !ULTRA_HIGH_FREQUENCIES.includes(hz))
    : HZ_VALUES;

export const clipToVisibleFrequencies = <T>(
  row: T[] | undefined,
  hideUltraHigh: boolean
): T[] | undefined => {
  if (!hideUltraHigh || !row) return row;
  return getVisibleFrequencies(true).map((hz) => row[HZ_VALUES.indexOf(hz)]);
};

// Device pixel ratio for Chart.js audiogram/tympanometry canvases that get
// rasterized for print/export. Must match the html-to-image `pixelRatio` used by
// nodeToPng in utils/common.ts — otherwise html-to-image upscales the low-res
// canvas bitmap and the chart looks blurry in the PDF (SN-18855).
export const AUDIOGRAM_PRINT_DEVICE_PIXEL_RATIO = 3;

// Snap an arbitrary frequency to the nearest audiometric frequency in
// log2 space. Returns null for invalid or out-of-range inputs so callers
// (e.g. the audiogram click-to-plot handler) can bail out cleanly; without
// this guard, Math.log2 of a non-positive number is NaN and the reduce
// below would silently return the first element.
export const snapToNearestFrequency = (rawHz: number): number | null => {
  if (!Number.isFinite(rawHz) || rawHz <= 0) return null;
  const MIN_HZ = HZ_VALUES[0];
  const MAX_HZ = HZ_VALUES.at(-1) as number;
  if (rawHz < MIN_HZ * 0.9 || rawHz > MAX_HZ * 1.1) return null;
  return HZ_VALUES.reduce(
    (prev, curr) =>
      Math.abs(Math.log2(curr) - Math.log2(rawHz)) <
      Math.abs(Math.log2(prev) - Math.log2(rawHz))
        ? curr
        : prev,
    MIN_HZ
  );
};

const legendValues = {
  ACUL: "AC Unmasked (L)",
  BCUL: "BC Unmasked (L)",
  ACML: "AC Masked (L)",
  BCML: "BC Masked (L)",
  ACUR: "AC Unmasked (R)",
  BCUR: "BC Unmasked (R)",
  ACMR: "AC Masked (R)",
  BCMR: "BC Masked (R)",
  MCL: "MCL",
  UCL: "UCL",
  A: "Soundfield Aided",
  S: "Soundfield Unaided"
};

export const LEGEND_VALUES = new EnumDataSource(legendValues);
